import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { Building2, BarChart3, CreditCard, Inbox, User, ChevronLeft, ChevronRight, X, BadgeCheck, Megaphone } from "lucide-react";
import Logo from "./Logo.js";
import { auth, db, isAdmin } from "../firebase.js";

// Kijkt of het ingelogde account een dienstvermelding heeft, zodat we het
// "Dienst"-menu alleen tonen aan dienstaanbieders (niet aan feestlocaties).
function useHasService() {
  const [hasService, setHasService] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user || user.isAnonymous) {
        if (!cancelled) setHasService(false);
        return;
      }
      try {
        const snap = await getDocs(query(collection(db, "services"), where("uid", "==", user.uid)));
        if (!cancelled) setHasService(!snap.empty);
      } catch (_e) {
        if (!cancelled) setHasService(false);
      }
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  return hasService;
}

function getNavSections(hasService) {
  const sections = [
    {
      label: "Hoofdmenu",
      items: [
        { to: "/venues", label: "Locaties", icon: Building2 },
        { to: "/requests", label: "Aanvragen", icon: Inbox },
        { to: "/stats", label: "Statistieken", icon: BarChart3 },
      ],
    },
    {
      label: "Beheer",
      items: [
        { to: "/membership", label: "Abonnementen", icon: CreditCard },
        { to: "/account", label: "Account", icon: User },
      ],
    },
  ];

  if (hasService) {
    sections.splice(1, 0, {
      label: "Dienst",
      items: [
        { to: "/mijn-dienst", label: "Mijn dienst", icon: Megaphone },
      ],
    });
  }

  if (isAdmin()) {
    sections.push({
      label: "Admin",
      items: [{ to: "/diensten-goedkeuren", label: "Diensten goedkeuren", icon: BadgeCheck }],
    });
  }

  return sections;
}

function NavItem({ to, label, icon: Icon, collapsed, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        [
          "group flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-fast relative",
          isActive
            ? "bg-accent-subtle text-accent before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:bg-accent before:rounded-full"
            : "text-[rgba(255,255,255,0.6)] hover:text-white hover:bg-[rgba(255,255,255,0.06)]",
          collapsed ? "lg:justify-center" : "",
        ].join(" ")
      }
      title={collapsed ? label : undefined}
    >
      <Icon size={20} strokeWidth={1.5} className="shrink-0" />
      <span className={collapsed ? "lg:hidden" : ""}>{label}</span>
    </NavLink>
  );
}

function MainNav({ isPublic, collapsed, onToggleCollapse, mobileOpen, onCloseMobile }) {
  const hasService = useHasService();

  if (isPublic) return null;

  const navSections = getNavSections(hasService);

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={onCloseMobile}
        />
      )}

      <aside
        className={[
          "fixed top-0 left-0 h-full bg-bg-sidebar z-40",
          "flex flex-col transition-all duration-normal",
          mobileOpen ? "translate-x-0 w-sidebar" : "-translate-x-full",
          "lg:translate-x-0",
          collapsed ? "lg:w-sidebar-collapsed" : "lg:w-sidebar",
        ].join(" ")}
      >
        <div className={`flex items-center h-topbar px-4 ${collapsed ? "lg:justify-center" : ""} justify-between`}>
          {(!collapsed || mobileOpen) && (
            <span className={`text-white ${collapsed ? "lg:hidden" : ""}`}>
              <Logo />
            </span>
          )}
          <button
            onClick={onCloseMobile}
            className="p-1.5 rounded-sm text-[rgba(255,255,255,0.4)] hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-colors duration-fast lg:hidden"
            title="Menu sluiten"
          >
            <X size={18} />
          </button>
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded-sm text-[rgba(255,255,255,0.4)] hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-colors duration-fast hidden lg:block"
            title={collapsed ? "Menu uitklappen" : "Menu inklappen"}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
          {navSections.map((section) => (
            <div key={section.label}>
              <p className={`px-3 mb-2 text-xs font-medium uppercase tracking-wider text-[rgba(255,255,255,0.3)] ${collapsed ? "lg:hidden" : ""}`}>{section.label}</p>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavItem key={item.to} {...item} collapsed={collapsed} onClick={onCloseMobile} />
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}

export default MainNav;
